import type { RepwiseEvent } from '@repwise/domain';
import { Dexie, type Table } from 'dexie';
import { hashEvent } from './canonical.ts';
import { uuidv7 } from './uuid.ts';

/** One event in the local log. `serverSeq` is set once the server has it (pushed or pulled). */
export interface EventRow {
  id: string;
  ownerId: string;
  deviceId: string;
  deviceSeq: number;
  serverSeq?: number;
  at: number;
  type: string;
  workoutId: string | null;
  hash: string;
  payload: RepwiseEvent;
}

export interface OutboxRow {
  id: string;
  ownerId: string;
  deviceSeq: number;
  attempts: number;
  nextAttemptAt: number;
  lastError?: string;
}

export interface DeadLetterRow {
  id: string;
  ownerId: string;
  at: number;
  reason: 'conflict' | 'rejected' | 'pull_conflict' | 'discarded';
  payload: RepwiseEvent;
  detail?: string;
}

export interface Profile {
  id: string;
  /** Supabase user id this local profile belongs to, once claimed (ADR-0004 hardening). */
  claimedBy?: string;
  createdAt: number;
}

interface MetaRow {
  key: string;
  value: unknown;
}

class RepwiseDb extends Dexie {
  events!: Table<EventRow, string>;
  outbox!: Table<OutboxRow, string>;
  deadLetter!: Table<DeadLetterRow, string>;
  profiles!: Table<Profile, string>;
  meta!: Table<MetaRow, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      events: 'id, ownerId, [ownerId+serverSeq], [ownerId+deviceSeq]',
      outbox: 'id, ownerId, deviceSeq, nextAttemptAt',
      deadLetter: 'id, ownerId, at',
      profiles: 'id, claimedBy',
      meta: 'key',
    });
  }
}

const workoutIdOf = (e: RepwiseEvent): string | null => ('workoutId' in e ? (e as { workoutId: string }).workoutId : null);

/**
 * The local, offline-first store. Every write is one IndexedDB transaction (event + outbox + device seq),
 * with hashes computed BEFORE the transaction: awaiting WebCrypto inside a Dexie transaction would commit it.
 */
export class RepwiseStore {
  readonly db: RepwiseDb;
  private listeners = new Set<() => void>();

  private constructor(name: string) {
    this.db = new RepwiseDb(name);
  }

  static async open(name = 'repwise'): Promise<RepwiseStore> {
    const s = new RepwiseStore(name);
    await s.db.open();
    await s.ensureDevice();
    return s;
  }

  close(): void {
    this.db.close();
  }

  /** Remove this device's Repwise database entirely (J8 "remove my data", J13 account deletion). */
  async destroy(): Promise<void> {
    this.gen++;
    this.cache = null;
    this.db.close();
    await this.db.delete();
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  /**
   * The active profile's log in order, kept in memory (render-cost fix, ADR-0024): a local append extends it in place
   * (new local events always sort last), and any other change (pull, push acknowledgements, profile switch) drops it.
   */
  private cache: { profile: string; events: RepwiseEvent[] } | null = null;
  /** Bumped by every write: a read may only fill the cache if nothing was written while it ran. */
  private gen = 0;

  /** Data changed outside append(): forget the cached order and tell listeners. */
  notify(): void {
    this.gen++;
    this.cache = null;
    this.emit();
  }

  private emit(): void {
    for (const cb of this.listeners) cb();
  }

  async meta<T>(key: string): Promise<T | undefined> {
    return (await this.db.meta.get(key))?.value as T | undefined;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await this.db.meta.put({ key, value });
  }

  private async ensureDevice(): Promise<void> {
    await this.db.transaction('rw', this.db.meta, this.db.profiles, async () => {
      if (!(await this.db.meta.get('deviceId'))) await this.db.meta.put({ key: 'deviceId', value: uuidv7() });
      if (!(await this.db.meta.get('deviceSeq'))) await this.db.meta.put({ key: 'deviceSeq', value: 0 });
      if (!(await this.db.meta.get('activeProfile'))) {
        const p: Profile = { id: uuidv7(), createdAt: Date.now() };
        await this.db.profiles.put(p);
        await this.db.meta.put({ key: 'activeProfile', value: p.id });
      }
    });
  }

  async deviceId(): Promise<string> {
    return (await this.meta<string>('deviceId'))!;
  }

  async activeProfile(): Promise<Profile> {
    const id = (await this.meta<string>('activeProfile'))!;
    return (await this.db.profiles.get(id))!;
  }

  /** Append domain events to the local log and the outbox, atomically. */
  async append(events: readonly RepwiseEvent[]): Promise<void> {
    if (events.length === 0) return;
    const hashes = await Promise.all(events.map((e) => hashEvent(e)));
    const added: RepwiseEvent[] = [];
    let owner = '';
    this.gen++; // before the write: any read already in flight must not cache its (older) rows
    await this.db.transaction('rw', this.db.events, this.db.outbox, this.db.meta, async () => {
      const ownerId = (await this.db.meta.get('activeProfile'))!.value as string;
      owner = ownerId;
      const deviceId = (await this.db.meta.get('deviceId'))!.value as string;
      let seq = (await this.db.meta.get('deviceSeq'))!.value as number;
      for (let i = 0; i < events.length; i++) {
        const e = events[i]!;
        if (await this.db.events.get(e.id)) continue; // idempotent local append
        seq++;
        await this.db.events.add({ id: e.id, ownerId, deviceId, deviceSeq: seq, at: e.at, type: e.type, workoutId: workoutIdOf(e), hash: hashes[i]!, payload: e });
        added.push(e);
        await this.db.outbox.add({ id: e.id, ownerId, deviceSeq: seq, attempts: 0, nextAttemptAt: 0 });
      }
      await this.db.meta.put({ key: 'deviceSeq', value: seq });
    });
    // A read that ran after the commit may already hold these: then just re-read next time (never duplicate).
    this.gen++;
    const c = this.cache;
    const tail = c ? new Set(c.events.slice(-(added.length + 50)).map((e) => e.id)) : null;
    this.cache = c && c.profile === owner && !added.some((e) => tail!.has(e.id)) ? { profile: owner, events: [...c.events, ...added] } : null;
    this.emit();
  }

  /**
   * The active profile's log in protocol order (§5): server-sequenced events first by server_seq, then
   * unsynced local events by device_seq. This array order is what the domain fold treats as authoritative.
   */
  async events(): Promise<RepwiseEvent[]> {
    const p = await this.activeProfile();
    if (this.cache?.profile === p.id) return this.cache.events.slice();
    const gen = this.gen;
    const rows = await this.db.events.where('ownerId').equals(p.id).toArray();
    rows.sort((a, b) => {
      const sa = a.serverSeq ?? Number.MAX_SAFE_INTEGER;
      const sb = b.serverSeq ?? Number.MAX_SAFE_INTEGER;
      return sa !== sb ? sa - sb : a.deviceSeq - b.deviceSeq;
    });
    const events = rows.map((r) => r.payload);
    if (gen === this.gen) this.cache = { profile: p.id, events };
    return events.slice();
  }

  async pendingCount(): Promise<number> {
    const p = await this.activeProfile();
    return this.db.outbox.where('ownerId').equals(p.id).count();
  }

  async oldestPendingAt(): Promise<number | undefined> {
    const p = await this.activeProfile();
    const ids = (await this.db.outbox.where('ownerId').equals(p.id).primaryKeys()) as string[];
    if (!ids.length) return undefined;
    const rows = await this.db.events.bulkGet(ids);
    return rows.reduce<number | undefined>((m, r) => (r && (m === undefined || r.at < m) ? r.at : m), undefined);
  }

  async deadLetters(): Promise<DeadLetterRow[]> {
    const p = await this.activeProfile();
    return this.db.deadLetter.where('ownerId').equals(p.id).toArray();
  }

  /** The user has saved or no longer needs these unsyncable changes (Settings → Couldn't sync). */
  async dismissDeadLetters(ids: readonly string[]): Promise<void> {
    await this.db.deadLetter.bulkDelete([...ids]);
    this.notify();
  }

  /** Ask the browser not to evict our storage (iOS: only meaningful in the installed app). */
  static async requestPersistence(): Promise<boolean> {
    try {
      return (await globalThis.navigator?.storage?.persist?.()) ?? false;
    } catch {
      return false;
    }
  }
}
